/**
 * TVA Tab Component
 *
 * Displays and allows editing of VAT configuration.
 */

import { useMemo } from 'react';
import { EditableSelect } from '@/components/ui';
import { useCurrency } from '@/hooks';
import { formatPrice } from '@/lib/format';
import type { RawProductWithRelations } from '@bstock/shared';

import { VAT_OPTIONS } from '../../constants';

interface TvaTabProps {
  product: RawProductWithRelations;
  onChange: (field: string, value: unknown) => void;
}

export function TvaTab({ product, onChange }: TvaTabProps) {
  const currency = useCurrency();

  // Simulation de calcul (avec un prix fictif)
  const mockPrice = 10.0;
  const purchaseTvaAmount = useMemo(
    () => mockPrice * (product.purchaseVatRate / 100),
    [product.purchaseVatRate]
  );
  const purchaseTtcAmount = useMemo(() => mockPrice + purchaseTvaAmount, [purchaseTvaAmount]);

  return (
    <div className="space-y-6">
      {/* Configuration TVA */}
      <div className="bg-white border border-neutral-200 rounded-xl p-6 shadow-sm">
        <h3 className="text-base font-semibold text-dark-500 mb-4">
          TVA Achat
        </h3>

        <div>
          <label className="block text-sm font-medium text-neutral-600 mb-2">
            Taux de TVA
          </label>
          <EditableSelect
            value={product.purchaseVatRate.toString()}
            options={VAT_OPTIONS}
            onSave={(value) => onChange('purchaseVatRate', parseFloat(value))}
          />
        </div>
      </div>

      {/* Simulation */}
      <div className="bg-white border border-neutral-200 rounded-xl p-6 shadow-sm">
        <h3 className="text-base font-semibold text-dark-500 mb-4">
          Simulation (prix exemple: {formatPrice(mockPrice, currency)} HT)
        </h3>

        <div className="space-y-3">
          <div className="flex justify-between items-center py-2 border-b border-neutral-100">
            <span className="text-sm text-neutral-600">Prix HT</span>
            <span className="text-sm font-medium text-dark-500">
              {formatPrice(mockPrice, currency)}
            </span>
          </div>
          <div className="flex justify-between items-center py-2 border-b border-neutral-100">
            <span className="text-sm text-neutral-600">
              TVA ({product.purchaseVatRate}%)
            </span>
            <span className="text-sm font-medium text-dark-500">
              {formatPrice(purchaseTvaAmount, currency)}
            </span>
          </div>
          <div className="flex justify-between items-center py-2">
            <span className="text-sm font-medium text-dark-500">Prix TTC</span>
            <span className="text-lg font-semibold text-primary-600">
              {formatPrice(purchaseTtcAmount, currency)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
